/**
 * Analytics tracking utility for capturing user interactions
 * Sends events to the backend for storage and analysis
 */

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
const ANALYTICS_ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED !== "false";

class AnalyticsTracker {
  constructor() {
    this.sessionId = this._generateSessionId();
    this.sessionStartTime = Date.now();
    this.currentPage = null;
    this.pageStartTime = null;
    this.userProfile = null;
    this.pendingEvents = [];
    this.flushInterval = null;
    
    if (ANALYTICS_ENABLED) {
      this._startAutoFlush();
      // POST /analytics/events is PingOne-authorized, so the final flush uses a keepalive fetch
      // (which carries the token) instead of sendBeacon (which cannot).
      window.addEventListener("pagehide", () => { this.flush(); });
    }
  }

  _generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  }

  _startAutoFlush() {
    // Flush events every 10 seconds
    this.flushInterval = setInterval(() => {
      this.flush();
    }, 10000);
  }

  setUser(profile) {
    console.log('[Analytics] setUser called with:', profile);
    this.userProfile = profile;
    console.log('[Analytics] userProfile set to:', this.userProfile);
  }

  trackPageView(pageName) {
    console.log('[Analytics] trackPageView called with:', pageName, 'userProfile:', this.userProfile);
    
    // Skip tracking for the analytics page itself
    if (pageName === 'analytics') {
      console.log('[Analytics] trackPageView skipped - analytics page excluded');
      return;
    }
    
    // Track duration of previous page
    if (this.currentPage && this.pageStartTime) {
      this.trackEvent("page_duration", {
        page: this.currentPage,
        duration: Date.now() - this.pageStartTime,
      });
    }

    // Set new page
    this.currentPage = pageName;
    this.pageStartTime = Date.now();

    this.trackEvent("page_view", {
      page: pageName,
    });
  }

  trackEvent(eventType, eventData = {}) {
    if (!ANALYTICS_ENABLED) return;

    // Skip tracking if user is not authenticated
    if (!this.userProfile) {
      console.log('[Analytics] trackEvent skipped - user not authenticated');
      return;
    }

    const userEmail = this.userProfile?.preferred_username || this.userProfile?.email || "anonymous";
    console.log('[Analytics] trackEvent:', eventType, 'userEmail:', userEmail, 'userProfile:', this.userProfile);

    const event = {
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      eventType,
      eventData,
      page: this.currentPage,
      userEmail: userEmail,
      userName: this.userProfile?.name || this.userProfile?.given_name || "unknown",
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    this.pendingEvents.push(event);

    // Auto-flush if we have too many pending events
    if (this.pendingEvents.length >= 20) {
      this.flush();
    }
  }

  trackAction(actionName, details = {}) {
    this.trackEvent("user_action", {
      action: actionName,
      ...details,
    });
  }

  trackApiCall(endpoint, method, duration, success) {
    this.trackEvent("api_call", {
      endpoint,
      method,
      duration,
      success,
    });
  }

  trackError(error, context = {}) {
    this.trackEvent("error", {
      message: error?.message || String(error),
      context,
    });
  }

  async flush() {
    if (this.pendingEvents.length === 0) return;

    const eventsToSend = [...this.pendingEvents];
    this.pendingEvents = [];

    try {
      await fetch(`${API_BASE}/analytics/events`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ events: eventsToSend }),
      });
    } catch (error) {
      console.error("Failed to send analytics events:", error);
      // Don't re-queue events to avoid infinite loops
    }
  }

  getSessionDuration() {
    return Date.now() - this.sessionStartTime;
  }

  cleanup() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flush();
  }
}

// Create singleton instance
const analytics = new AnalyticsTracker();

export default analytics;
